import {
  ADMIN_RECORD_ROUTE_SEGMENTS,
  ASSISTANT_RECORD_TYPES,
  type AssistantRecordType,
} from "./attachments";

export const MAX_PAGE_ACTIONS = 40;
export const MAX_PAGE_ACTION_LABEL_CHARS = 80;
export const MAX_PAGE_ACTION_REASON_CHARS = 200;
export const MAX_PAGE_TITLE_CHARS = 160;
export const MAX_PAGE_HEADINGS = 20;
export const MAX_PAGE_HEADING_CHARS = 160;
export const MAX_PAGE_STATES = 60;
export const MAX_PAGE_TEXT_CHARS = 6_000;
export const MAX_PAGE_ELEMENTS = 200;
export const MAX_PAGE_ELEMENT_NAME_CHARS = 80;
export const MAX_PAGE_ELEMENT_VALUE_CHARS = 120;

export const RECORD_ACTION_ATTRIBUTE = "data-record-action";

export const RECORD_ACTIONS = {
  advance: "advance",
  sendPurchaseOrder: "send_purchase_order",
  sendBillOfLading: "send_bill_of_lading",
  changeVendor: "change_vendor",
  changeBuyer: "change_buyer",
  completeOrder: "complete_order",
  approveQuote: "approve_quote",
} as const;

export type RecordAction = (typeof RECORD_ACTIONS)[keyof typeof RECORD_ACTIONS];

const KNOWN_RECORD_ACTIONS: ReadonlySet<string> = new Set(Object.values(RECORD_ACTIONS));

export function isRecordAction(value: string | null | undefined): value is RecordAction {
  return value !== null && value !== undefined && KNOWN_RECORD_ACTIONS.has(value);
}

export interface PageAction {
  label: string;
  disabled: boolean;
  reason?: string;
  recordAction?: RecordAction;
}

export interface PageElement {
  ref: string;
  role: string;
  name: string;
  disabled: boolean;
  value?: string;
}

export type RawPageElement = PageElement;

export interface PageContent {
  title?: string;
  headings: string[];
  states: string[];
  elements: PageElement[];
  text?: string;
}

export interface PageSnapshot {
  actions: PageAction[];
  content?: PageContent;
}

export type PageRecordType = AssistantRecordType;

export interface PageRecord {
  type: PageRecordType;
  id: string;
}

const ADMIN_RECORD_ROUTES: ReadonlyArray<{ type: PageRecordType; pattern: RegExp }> = ASSISTANT_RECORD_TYPES.map(
  (type) => ({
    type,
    pattern: new RegExp(`^/admin/${ADMIN_RECORD_ROUTE_SEGMENTS[type]}/([a-z0-9]{20,40})(?:[/?#]|$)`),
  }),
);

export function adminRecordFromRoute(route: string | undefined): PageRecord | null {
  if (!route) return null;
  for (const { type, pattern } of ADMIN_RECORD_ROUTES) {
    const match = pattern.exec(route);
    if (match?.[1]) return { type, id: match[1] };
  }
  return null;
}

export function adminRecordUrl(record: PageRecord): string {
  return `/admin/${ADMIN_RECORD_ROUTE_SEGMENTS[record.type]}/${record.id}`;
}

const PARTY_JOURNEY_ROUTE = /^\/dashboard\/orders\/([a-z0-9]{20,40})(?:[/?#]|$)/;

export function partyJourneyIdFromRoute(route: string | undefined): string | undefined {
  return route ? PARTY_JOURNEY_ROUTE.exec(route)?.[1] : undefined;
}

function clampText(value: string | undefined, maxChars: number): string | undefined {
  const flat = value?.replace(/\s+/g, " ").trim();
  return flat ? flat.slice(0, maxChars) : undefined;
}

function clampPageText(value: string | undefined): string | undefined {
  const lines = (value ?? "")
    .split("\n")
    .map((line) => line.replace(/[^\S\n]+/g, " ").trim())
    .filter((line) => line.length > 0);
  const text = lines.join("\n").slice(0, MAX_PAGE_TEXT_CHARS);
  return text || undefined;
}

export interface RawPageContent {
  title?: string;
  headings: ReadonlyArray<string>;
  states?: ReadonlyArray<string>;
  elements?: ReadonlyArray<RawPageElement>;
  text?: string;
}

export interface RawPageSnapshot {
  actions: ReadonlyArray<Omit<PageAction, "recordAction"> & { recordAction?: string }>;
  content?: RawPageContent;
}

const MAX_RAW_PAGE_ACTIONS = 400;
const MAX_RAW_PAGE_TEXT_CHARS = 2_000;
const MAX_RAW_PAGE_HEADINGS = 200;
const MAX_RAW_PAGE_BODY_CHARS = 4 * MAX_PAGE_TEXT_CHARS;

function contentWithinLimits(content: RawPageContent | undefined): boolean {
  if (!content) return true;
  return (
    (content.title?.length ?? 0) <= MAX_RAW_PAGE_TEXT_CHARS &&
    (content.text?.length ?? 0) <= MAX_RAW_PAGE_BODY_CHARS &&
    content.headings.length <= MAX_RAW_PAGE_HEADINGS &&
    content.headings.every((heading) => heading.length <= MAX_RAW_PAGE_TEXT_CHARS) &&
    (content.states?.length ?? 0) <= MAX_RAW_PAGE_ACTIONS &&
    (content.states ?? []).every((state) => state.length <= MAX_RAW_PAGE_TEXT_CHARS) &&
    (content.elements?.length ?? 0) <= MAX_RAW_PAGE_ACTIONS * 2 &&
    (content.elements ?? []).every(
      (element) =>
        element.ref.length <= MAX_PAGE_ELEMENT_NAME_CHARS &&
        element.role.length <= MAX_PAGE_ELEMENT_NAME_CHARS &&
        element.name.length <= MAX_RAW_PAGE_TEXT_CHARS &&
        (element.value?.length ?? 0) <= MAX_RAW_PAGE_TEXT_CHARS,
    )
  );
}

export function pageSnapshotWithinLimits(snapshot: RawPageSnapshot): boolean {
  return (
    snapshot.actions.length <= MAX_RAW_PAGE_ACTIONS &&
    snapshot.actions.every(
      (action) =>
        action.label.length <= MAX_RAW_PAGE_TEXT_CHARS && (action.reason?.length ?? 0) <= MAX_RAW_PAGE_TEXT_CHARS,
    ) &&
    contentWithinLimits(snapshot.content)
  );
}

function clampLines(lines: ReadonlyArray<string>, maxLines: number): string[] {
  return [
    ...new Set(lines.flatMap((line) => clampText(line, MAX_PAGE_HEADING_CHARS) ?? [])),
  ].slice(0, maxLines);
}

function clampPageElement(element: RawPageElement): PageElement {
  const value = clampText(element.value, MAX_PAGE_ELEMENT_VALUE_CHARS);
  return {
    ref: element.ref,
    role: element.role,
    name: clampText(element.name, MAX_PAGE_ELEMENT_NAME_CHARS) ?? "",
    disabled: element.disabled,
    ...(value ? { value } : {}),
  };
}

function clampPageContent(content: RawPageContent): PageContent {
  const title = clampText(content.title, MAX_PAGE_TITLE_CHARS);
  const text = clampPageText(content.text);
  return {
    headings: clampLines(content.headings, MAX_PAGE_HEADINGS),
    states: clampLines(content.states ?? [], MAX_PAGE_STATES),
    elements: (content.elements ?? []).slice(0, MAX_PAGE_ELEMENTS).map(clampPageElement),
    ...(title ? { title } : {}),
    ...(text ? { text } : {}),
  };
}

function actionPriority(action: RawPageSnapshot["actions"][number]): number {
  if (isRecordAction(action.recordAction)) return 0;
  return action.disabled ? 1 : 2;
}

export function clampPageSnapshot(snapshot: RawPageSnapshot): PageSnapshot {
  const actions: PageAction[] = [];
  const seen = new Set<string>();
  const ordered = [...snapshot.actions].sort((left, right) => actionPriority(left) - actionPriority(right));
  for (const action of ordered) {
    const label = clampText(action.label, MAX_PAGE_ACTION_LABEL_CHARS);
    if (!label) continue;
    const reason = action.disabled ? clampText(action.reason, MAX_PAGE_ACTION_REASON_CHARS) : undefined;
    const recordAction = isRecordAction(action.recordAction) ? action.recordAction : undefined;
    const key = `${label}|${action.disabled}|${reason ?? ""}|${recordAction ?? ""}`;
    if (seen.has(key)) continue;
    seen.add(key);
    actions.push({
      label,
      disabled: action.disabled,
      ...(reason ? { reason } : {}),
      ...(recordAction ? { recordAction } : {}),
    });
    if (actions.length >= MAX_PAGE_ACTIONS) break;
  }
  return snapshot.content ? { actions, content: clampPageContent(snapshot.content) } : { actions };
}

export function acceptPageSnapshot(snapshot: RawPageSnapshot | undefined): PageSnapshot | undefined {
  return snapshot && pageSnapshotWithinLimits(snapshot) ? clampPageSnapshot(snapshot) : undefined;
}
